/**
 * REGISTRE ET « À VALIDER » — le modèle pur (21/10/2026).
 *
 * Ce module ne parle à personne : il porte les formes que Sonde renvoie (contrat
 * v1 : discovery, registry, imports, proposals, relayées TELLES QUELLES par
 * Gestion), leurs gardes, et les règles qui décident de ce que l'écran propose.
 * Testé seul (tests/sonde-registre.test.ts), avec des données synthétiques.
 *
 * Règles de Sonde reprises ici (jamais réinventées) :
 *   · VLAN 30 (téléphones du personnel) et 40 (invités) : seulement comptés,
 *     jamais suivis un par un — la seule décision possible est « Ignorer » ;
 *   · `attach_hint.replacement` : exemplaire remplacé → rattacher avec
 *     `replace: true` (l'ancien identifiant est clos, l'historique suit le poste) ;
 *   · un uid de poste : `^[a-z0-9][a-z0-9-]{1,62}$`, immuable ;
 *   · un lot de décisions : 200 au plus ;
 *   · les gestes de Sonde elle-même ont l'acteur « système ».
 */

/* ═══════════════ Formes de Sonde (contrat v1) ═══════════════ */

export type ActionProposee = 'accept' | 'attach' | 'review'
export type StatutDecouverte = 'pending' | 'accepted' | 'ignored'

export interface IndiceRattachement {
  uid: string
  display_name: string
  lifecycle: string
  current_values: string[]
  replacement: boolean
  reason_fr: string
}

export interface Decouverte {
  id: string
  scheme: string
  value: string
  vlan: number | null
  oui: string | null
  vendor: string | null
  hostname: string | null
  ip: string | null
  first_seen_at: string
  last_seen_at: string
  seen_count: number
  source: string | null
  suggested: { kind: string | null; family: string | null; uid: string | null; zone_code: string | null; name: string | null }
  confidence: number | null
  local_mac: boolean
  status: StatutDecouverte
  decided_by: string | null
  decided_at: string | null
  device_uid: string | null
  evidence: Record<string, unknown>
  proposed_action: ActionProposee | null
  attach_hint: IndiceRattachement | null
}

export interface ListeDecouvertes {
  items: Decouverte[]
  counts: { pending: number; accepted: number; ignored: number }
  next_cursor: string | null
}

export interface IssueDecision {
  id: string
  action: 'accept' | 'attach' | 'ignore' | 'reset'
  ok: boolean
  device_uid: string | null
  code: string | null
  message_fr: string
}

export interface ResultatDecisions { results: IssueDecision[]; applied: number; failed: number }

export interface EtatAppareil {
  display_state: string
  display_label: string
  greyed: boolean
  since: string | null
  observed_at: string | null
  data_age_s: number | null
  exception: { kind: string; reason: string; until: string | null } | null
}

export interface AppareilBref {
  uid: string
  display_name: string
  kind: string
  family: string
  family_label: string
  zone_code: string | null
  zone_detail: string | null
  lifecycle: string
  criticality: string
  weighted: boolean
  visibility: string
  tracking: string
  state: EtatAppareil
}

export interface ListeAppareils {
  generated_at: string
  collector: { ok: boolean; stale: boolean; heartbeat_age_s: number | null; code: string | null; detail_fr: string | null }
  total: number
  items: AppareilBref[]
  next_cursor: string | null
}

export interface PosteRegistre {
  id: string
  uid: string
  kind: string
  family: string
  display_name: string
  zone_code: string | null
  zone_detail: string | null
  lifecycle: string
  criticality: string
  affects_global_health: boolean
  visibility: string
  tracking: string
  profile_code: string | null
  power_domain_code: string | null
  /** Alimentations A/B (contrat du 27/10/2026) : `ups_uid` null = secteur seul. Absent d'une Sonde plus ancienne. */
  power_feeds?: AlimentationRegistre[]
  tags: string[]
  notes: string | null
  first_seen_at: string | null
  last_seen_at: string | null
  created_at: string
  updated_at: string
  retired_at: string | null
  etag: string
}

export interface AlimentationRegistre { feed: 'A' | 'B'; ups_uid: string | null }

export interface IdentifiantRegistre {
  id: string
  device_uid: string
  scheme: string
  value: string
  vlan: number | null
  valid_from: string
  valid_to: string | null
  current: boolean
  source: string | null
  confidence: number
  confirmed_by: string | null
}

export interface LienRegistre {
  id: string
  kind: string
  kind_label: string
  from_uid: string
  from_name: string
  to_uid: string
  to_name: string
  attrs: Record<string, unknown>
  origin: 'auto' | 'declared'
  source: string | null
  confidence: number
  valid_from: string
  valid_to: string | null
  current: boolean
  confirmed_by: string | null
  etag: string
}

export interface ExceptionRegistre {
  id: string
  device_uid: string
  kind: string
  kind_label: string
  reason: string
  starts_at: string
  ends_at: string
  status: 'scheduled' | 'active' | 'ended' | 'cancelled'
  created_by: string
  cancelled_by: string | null
  etag: string
}

export interface LienParc {
  id: string
  parc_item_id: string
  origin: 'suggested' | 'confirmed' | 'rejected'
  confidence: number | null
  confirmed_by: string | null
  confirmed_at: string | null
  valid_from: string
  valid_to: string | null
  snapshot: Record<string, unknown> | null
}

export interface FicheEdition {
  device: PosteRegistre
  identifiers: IdentifiantRegistre[]
  links: { upstream: LienRegistre[]; downstream: LienRegistre[] }
  overrides: ExceptionRegistre[]
  parc_links: LienParc[]
  pending_discoveries: { id: string; scheme: string; value: string; last_seen_at: string; reason_fr: string }[]
}

export interface FenetreMaintenance {
  id: string
  scope_kind: string
  scope_ref: string
  scope_label: string
  starts_at: string
  ends_at: string
  reason: string
  status: 'scheduled' | 'active' | 'ended' | 'cancelled'
  created_by: string
  cancelled_by: string | null
  etag: string
}

export interface SalleRegistre { code: string; label: string; kind: string; sort_order: number; device_count: number }

export type ActionImport = 'create' | 'update' | 'attach' | 'replace' | 'conflict'

export interface ElementImport {
  id: string
  entity: 'device' | 'identifier' | 'link'
  action: ActionImport
  uid: string
  line: number | null
  summary_fr: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  applicable: boolean
  reason_fr: string | null
}

export interface ResultatImport {
  mode: 'preview' | 'apply'
  preview_hash: string
  rows: { total: number; parsed: number; rejected: number }
  counts: {
    create_device: number; update_device: number; attach_identifier: number; replace_identifier: number
    create_link: number; replace_link: number; conflict: number; unchanged: number
  }
  items: ElementImport[]
  issues: { line: number | null; fiche: number | null; level: 'error' | 'warning' | 'info'; message_fr: string }[]
  applied: {
    devices_created: number; devices_updated: number; identifiers_attached: number; identifiers_closed: number
    links_created: number; links_closed: number; excluded: number; zones_seeded: string[]
  } | null
}

export type SorteProposition = 'parc_signalement' | 'retire_ghost' | 'replaced_unit' | 'power_domain'

export interface Proposition {
  id: string
  kind: SorteProposition
  kind_label: string
  status: 'proposed' | 'accepted' | 'dismissed' | 'expired'
  reason: string
  evidence: Record<string, unknown>
  created_at: string
  decided_at: string | null
  decided_by: string | null
  dismiss_reason: string | null
  reproposal_after: string | null
  device: { uid: string; display_name: string; kind: string; family: string; zone_code: string | null; parc_item_id: string | null } | null
  accept_effect_fr: string
}

export interface ListePropositions {
  items: Proposition[]
  counts: { proposed: number; accepted: number; dismissed: number; expired: number }
  next_cursor: string | null
}

/* ═══════════════ Gardes de forme ═══════════════ */

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const texte = (x: unknown): x is string => typeof x === 'string'
const texteOuNul = (x: unknown) => x === null || typeof x === 'string'
const entier = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x)

export function estDecouverte(x: unknown): x is Decouverte {
  return objet(x) && texte(x['id']) && texte(x['scheme']) && texte(x['value']) && objet(x['suggested'])
    && (x['status'] === 'pending' || x['status'] === 'accepted' || x['status'] === 'ignored')
    && objet(x['evidence']) && (x['attach_hint'] === null || objet(x['attach_hint']))
    && (x['vlan'] === null || entier(x['vlan']))
}

export function estListeDecouvertes(x: unknown): x is ListeDecouvertes {
  return objet(x) && Array.isArray(x['items']) && (x['items'] as unknown[]).every(estDecouverte)
    && objet(x['counts']) && entier(x['counts']['pending']) && texteOuNul(x['next_cursor'])
}

export function estResultatDecisions(x: unknown): x is ResultatDecisions {
  return objet(x) && Array.isArray(x['results']) && entier(x['applied']) && entier(x['failed'])
    && (x['results'] as unknown[]).every((r) => objet(r) && texte(r['id']) && typeof r['ok'] === 'boolean' && texte(r['message_fr']))
}

export function estAppareilBref(x: unknown): x is AppareilBref {
  return objet(x) && texte(x['uid']) && texte(x['display_name']) && objet(x['state']) && texte(x['state']['display_state'])
}

export function estListeAppareils(x: unknown): x is ListeAppareils {
  return objet(x) && Array.isArray(x['items']) && (x['items'] as unknown[]).every(estAppareilBref)
    && entier(x['total']) && texteOuNul(x['next_cursor']) && objet(x['collector'])
}

export function estFicheEdition(x: unknown): x is FicheEdition {
  if (!objet(x) || !objet(x['device'])) return false
  const d = x['device']
  return texte(d['uid']) && texte(d['etag']) && texte(d['display_name'])
    && Array.isArray(x['identifiers']) && objet(x['links']) && Array.isArray(x['links']['upstream']) && Array.isArray(x['links']['downstream'])
    && Array.isArray(x['overrides']) && Array.isArray(x['parc_links']) && Array.isArray(x['pending_discoveries'])
}

export function estListeMaintenances(x: unknown): x is { windows: FenetreMaintenance[] } {
  return objet(x) && Array.isArray(x['windows']) && (x['windows'] as unknown[]).every((w) => objet(w) && texte(w['id']) && texte(w['starts_at']))
}

export function estListeSalles(x: unknown): x is { zones: SalleRegistre[] } {
  return objet(x) && Array.isArray(x['zones']) && (x['zones'] as unknown[]).every((z) => objet(z) && texte(z['code']) && texte(z['label']))
}

export function estResultatImport(x: unknown): x is ResultatImport {
  return objet(x) && (x['mode'] === 'preview' || x['mode'] === 'apply') && texte(x['preview_hash'])
    && Array.isArray(x['items']) && Array.isArray(x['issues']) && objet(x['counts']) && objet(x['rows'])
    && (x['items'] as unknown[]).every((i) => objet(i) && texte(i['id']) && texte(i['action']) && typeof i['applicable'] === 'boolean')
}

export function estListePropositions(x: unknown): x is ListePropositions {
  return objet(x) && Array.isArray(x['items']) && objet(x['counts']) && texteOuNul(x['next_cursor'])
    && (x['items'] as unknown[]).every((p) => objet(p) && texte(p['id']) && texte(p['kind']) && texte(p['accept_effect_fr']))
}

/* ═══════════════ Libellés ═══════════════ */

export const LIBELLE_TYPE: Record<string, string> = {
  gateway: 'Passerelle', switch: 'Switch', switch_hikvision: 'Switch Hikvision', access_point: 'Borne Wi-Fi', cloud_key: 'Cloud Key',
  camera_unifi: 'Caméra UniFi', camera_hikvision: 'Caméra Hikvision', camera_wifi: 'Caméra Wi-Fi', nvr_unifi: 'NVR UniFi',
  nvr_hikvision: 'NVR Hikvision', viewport: 'Viewport', speaker: 'Haut-parleur', access_hub: 'Boîtier de porte Access',
  access_reader: 'Lecteur Access', connect_display: 'Écran Connect', luminode: 'LumiNode', lighting_console: 'Console lumière',
  strobe: 'Stroboscope', lighting_fixture: 'Lumière (projecteur, lampe)', dmx_node: 'Nœud DMX', sound_meter: 'Sonomètre', amplifier: 'Amplificateur',
  mixer: 'Console son', dsp: 'Processeur son', dj_player: 'Lecteur DJ', dj_mixer: 'Table de mixage DJ', payment_terminal: 'Terminal de paiement',
  pos_register: 'Caisse', iot_sensor: 'Capteur ou module connecté', smoke_detector: 'Détecteur de fumée', energy_meter: 'Compteur d’énergie',
  ups: 'Onduleur', server: 'Serveur', bmc: 'Carte d’administration', container: 'Conteneur', web_service: 'Service web',
  stream: 'Flux vidéo', printer: 'Imprimante', office_pc: 'Ordinateur', tablet: 'Tablette', other: 'Autre',
}

/** Pluriel des groupes « À valider » (« 22 détecteurs de fumée »). */
const PLURIEL_TYPE: Record<string, string> = {
  energy_meter: 'Compteurs d’énergie', smoke_detector: 'Détecteurs de fumée', iot_sensor: 'Capteurs et modules connectés', camera_hikvision: 'Caméras Hikvision',
  camera_unifi: 'Caméras UniFi', camera_wifi: 'Caméras Wi-Fi', viewport: 'Viewports', access_reader: 'Lecteurs Access',
  access_hub: 'Boîtiers de porte Access', nvr_hikvision: 'NVR Hikvision', nvr_unifi: 'NVR UniFi', payment_terminal: 'Terminaux de paiement',
  web_service: 'Services web', other: 'Autres', access_point: 'Bornes Wi-Fi', switch: 'Switchs', container: 'Conteneurs',
  lighting_fixture: 'Lumières',
}

export const libelleType = (k: string | null | undefined): string => (k ? LIBELLE_TYPE[k] ?? k : 'Type à choisir')
export const libelleGroupeType = (k: string | null | undefined): string => (k ? PLURIEL_TYPE[k] ?? LIBELLE_TYPE[k] ?? k : 'Type à choisir')

export const TYPES_POSTE: readonly string[] = Object.keys(LIBELLE_TYPE)

/** Mots que la recherche du choix de type reconnaît en plus du libellé (« ampoule » → Lumière). */
export const MOTS_TYPE: Record<string, string> = {
  lighting_fixture: 'lumiere eclairage lampe ampoule spot led ruban projecteur', strobe: 'lumiere flash', lighting_console: 'lumiere grandma',
  luminode: 'lumiere dmx artnet', dmx_node: 'lumiere artnet', iot_sensor: 'module prise relais shelly tuya thermometre temperature objet',
  smoke_detector: 'fumee incendie co alarme', energy_meter: 'electricite tableau td tarif puissance', ups: 'onduleur batterie eaton',
  payment_terminal: 'tpe carte bancaire a920', pos_register: 'caisse zap pyms', connect_display: 'ecran caisse', viewport: 'ecran video',
  camera_unifi: 'video', camera_hikvision: 'video', camera_wifi: 'video', nvr_unifi: 'enregistreur video', nvr_hikvision: 'enregistreur video',
  access_hub: 'porte acces', access_reader: 'porte badge interphone acces', speaker: 'son horn', sound_meter: 'son decibel db metre',
  office_pc: 'pc poste bureau', bmc: 'idrac', switch: 'reseau', switch_hikvision: 'reseau poe video', access_point: 'wifi reseau',
}

/** Famille d'un type, pour la recherche et l'aide (« Lumière », « Paiement »…). */
export const FAMILLE_TYPE: Record<string, string> = {
  gateway: 'network', switch: 'network', switch_hikvision: 'video', access_point: 'wifi', cloud_key: 'network', camera_unifi: 'video',
  camera_hikvision: 'video', camera_wifi: 'video', nvr_unifi: 'video', nvr_hikvision: 'video', viewport: 'video', speaker: 'sound',
  access_hub: 'access', access_reader: 'access', connect_display: 'display', luminode: 'lighting', lighting_console: 'lighting',
  strobe: 'lighting', lighting_fixture: 'lighting', dmx_node: 'lighting', sound_meter: 'sound', amplifier: 'sound', mixer: 'sound',
  dsp: 'sound', dj_player: 'dj', dj_mixer: 'dj', payment_terminal: 'payment', pos_register: 'pos', iot_sensor: 'iot',
  smoke_detector: 'fire_safety', energy_meter: 'power', ups: 'power', server: 'server', bmc: 'server', container: 'service',
  web_service: 'service', stream: 'video', printer: 'printing', office_pc: 'server', tablet: 'display',
}

/**
 * NIVEAU D'URGENCE proposé pour un type (criticité de Sonde). Ce qui fait
 * tourner la soirée (paiement, caisses, portes, enregistreurs, serveur, son)
 * est « Critique en soirée » ; le confort (lumières d'ambiance, prises,
 * modules) ne fait pas baisser l'état général s'il lâche.
 */
export function urgenceParDefaut(kind: string | null | undefined): string {
  switch (kind) {
    case 'payment_terminal': case 'pos_register': case 'access_hub': case 'access_reader': case 'nvr_unifi': case 'nvr_hikvision':
    case 'server': case 'ups': case 'sound_meter': case 'luminode': case 'smoke_detector': case 'gateway': case 'switch': case 'access_point':
      return 'critical_evening'
    case 'lighting_fixture': case 'iot_sensor':
      return 'comfort'
    case 'printer': case 'office_pc': case 'tablet': case 'container':
      return 'info'
    default:
      return 'important'
  }
}
/** « Confort » et « Information » ne pèsent pas dans l'état général. */
export const pesePourUrgence = (u: string): boolean => u === 'critical_evening' || u === 'important'

export const LIBELLE_FAMILLE: Record<string, string> = {
  network: 'Réseau', wifi: 'Wi-Fi', video: 'Vidéo', access: 'Contrôle d’accès', lighting: 'Lumière', sound: 'Son', dj: 'DJ',
  pos: 'Caisse', payment: 'Paiement', iot: 'Capteurs', fire_safety: 'Sécurité incendie', server: 'Serveur', service: 'Services',
  power: 'Énergie', display: 'Affichage', printing: 'Impression',
}

export const LIBELLE_SOURCE: Record<string, string> = {
  unifi_network: 'UniFi Network', unifi_protect: 'UniFi Protect', unifi_protect_local: 'UniFi Protect (local)', unifi_access: 'UniFi Access',
  hikvision_nvr_201: 'NVR Hikvision 201', hikvision_nvr_202: 'NVR Hikvision 202', hikvision_nvr_203: 'NVR Hikvision 203',
  hikvision_switches: 'Switchs Hikvision', home_assistant: 'Home Assistant', idrac: 'iDRAC', nut: 'Onduleur', host: 'Serveur',
  host_network: 'Réseau de l’hôte', docker: 'Docker', mediamtx: 'MediaMTX', npm: 'Nginx Proxy Manager', endpoints: 'URL des services',
  backups_server: 'Sauvegarde du serveur', luminode: 'LumiNode', sound_meters: 'Sonomètres', icmp: 'Ping', internet: 'Internet',
  parc: 'Parc', gestion: 'Gestion', sniffer: 'Écoute passive', sonde_self: 'Sonde', discovery: 'Découverte', techlc: 'Import TECH_LC',
}
export const libelleSource = (s: string | null | undefined): string => (s ? LIBELLE_SOURCE[s] ?? s : 'Source inconnue')

export const LIBELLE_SCHEMA: Record<string, string> = {
  mac: 'MAC', ip: 'IP', unifi_device_mac: 'MAC UniFi (équipement)', unifi_client_mac: 'MAC UniFi (client)', protect_id: 'Identifiant Protect',
  access_device_id: 'Identifiant Access', hik_nvr_channel: 'Voie NVR Hikvision', hik_device_no: 'N° Hikvision', ha_entity_id: 'Entité Home Assistant',
  techlc_fiche: 'Fiche TECH_LC', parc_item_id: 'Fiche Parc', serial: 'N° de série', artnet_ip: 'IP Art-Net', sacn_cid: 'CID sACN',
  prodjlink_name: 'Nom Pro DJ Link', docker_container: 'Conteneur Docker', npm_host: 'Hôte NPM', mediamtx_path: 'Chemin MediaMTX', hostname: 'Nom d’hôte',
}
export const libelleSchema = (s: string): string => LIBELLE_SCHEMA[s] ?? s
/** Schémas qu'on peut rattacher à la main (la fiche Parc a sa propre voie). */
export const SCHEMAS_SAISISSABLES: readonly string[] = Object.keys(LIBELLE_SCHEMA).filter((s) => s !== 'parc_item_id')

export const LIBELLE_CRITICITE: Record<string, string> = {
  critical_evening: 'Critique en soirée', important: 'Important', comfort: 'Confort', info: 'Information',
}
export const LIBELLE_SUIVI: Record<string, string> = { presence: 'Présence', detailed: 'Détaillé', ignored: 'Non suivi' }
export const LIBELLE_VISIBILITE: Record<string, string> = { normal: 'Normale', detail_only: 'Dans le détail seulement', hidden: 'Masqué' }
export const LIBELLE_CYCLE: Record<string, string> = {
  active: 'Actif', spare: 'En réserve', retired: 'Retiré', discovered: 'Découvert', ignored: 'Ignoré',
}
export const LIBELLE_EXCEPTION: Record<string, string> = {
  unplugged: 'Débranché volontairement', maintenance: 'En maintenance', ignore: 'Ignoré', off_expected: 'Éteint comme prévu',
}
export const LIBELLE_LIEN: Record<string, string> = {
  uplink: 'Port montant', wifi_ap: 'Borne Wi-Fi', nvr_channel: 'Voie NVR', poe_power: 'Alimentation PoE',
  relay_power: 'Alimentation par relais', dmx_feed: 'Flux DMX', depends_on: 'Dépend de',
}
export const LIBELLE_STATUT_FENETRE: Record<string, string> = {
  scheduled: 'Prévue', active: 'En cours', ended: 'Terminée', cancelled: 'Annulée',
}

const LIBELLE_PREUVE: Record<string, string> = {
  domaine: 'Domaine', classe: 'Classe', seen_by: 'Vu par', voie: 'Voie', relais: 'Relais', protocole: 'Protocole', mac: 'MAC',
  model: 'Modèle', modele: 'Modèle', model_key: 'Référence', third_party: 'Tiers', protect_state: 'État Protect', type: 'Type',
  adopted: 'Adopté', connected: 'Connecté', probable_mac: 'MAC probable', access_online: 'En ligne (Access)', door: 'Porte',
  door_relay: 'Relais de porte', door_position: 'Position de porte', version: 'Version', source: 'Source', motif_ignore: 'Motif',
}

/** Une preuve lisible : libellé français, Oui/Non, « — » pour l'absent (jamais 0). */
export function preuvesLisibles(ev: Record<string, unknown>): { cle: string; libelle: string; valeur: string }[] {
  return Object.entries(ev).map(([cle, v]) => ({
    cle,
    libelle: LIBELLE_PREUVE[cle] ?? cle.replace(/_/g, ' '),
    valeur: v === null || v === undefined || v === '' ? '—'
      : typeof v === 'boolean' ? (v ? 'Oui' : 'Non')
        : typeof v === 'string' || typeof v === 'number' ? String(v)
          : JSON.stringify(v).slice(0, 160),
  }))
}

/** L'acteur d'un geste : Sonde elle-même devient « système ». */
export function acteur(nom: string | null | undefined): string {
  if (!nom) return '—'
  return /^(sonde\b|system\b|système\b)/i.test(nom.trim()) ? 'système' : nom
}

/* ═══════════════ Règles de la file « À valider » ═══════════════ */

/** VLAN « comptés seulement » (vie privée) : 30 personnel, 40 invités. */
export const VLAN_COMPTES_SEULEMENT: readonly number[] = [30, 40]

export function vlanCompteSeulement(vlan: number | null | undefined): boolean {
  return typeof vlan === 'number' && VLAN_COMPTES_SEULEMENT.includes(vlan)
}

export function raisonViePrivee(vlan: number | null | undefined): string | null {
  if (!vlanCompteSeulement(vlan)) return null
  return `Vu sur le VLAN ${vlan} (${vlan === 30 ? 'téléphones du personnel' : 'invités'}) : ces appareils sont seulement comptés, jamais suivis un par un, pour la vie privée. Seule « Ignorer » est possible.`
}

/** Peut-on ACCEPTER ou RATTACHER cette découverte ? (Ignorer reste toujours possible tant qu'elle est en attente.) */
export function peutValider(d: Decouverte): boolean {
  return d.status === 'pending' && !vlanCompteSeulement(d.vlan)
}

/** Le rattachement proposé est-il faisable ? Un poste retiré se réactive d'abord. */
export function rattachementPossible(d: Decouverte): boolean {
  return peutValider(d) && d.attach_hint !== null && d.attach_hint.lifecycle !== 'retired'
}

export const LOT_MAX = 200

export const UID_POSTE = /^[a-z0-9][a-z0-9-]{1,62}$/
export const CODE_SALLE = /^[A-Z0-9][A-Z0-9-]{0,31}$/

/** « Détecteur fumée — Salon (n°2) » → « detecteur-fumee-salon-n-2 » ; vide → préfixe du type. */
export function uidDepuisNom(nom: string | null | undefined, repli = 'poste'): string {
  const base = (nom ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63).replace(/-+$/g, '')
  if (UID_POSTE.test(base)) return base
  const r = repli.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'poste'
  return base ? `${r}-${base}`.slice(0, 63).replace(/-+$/g, '') : r.length >= 2 ? r : `${r}-1`
}

/** Rend les uid uniques DANS le lot (les doublons avec le registre, Sonde les refuse un par un : DEVICE_EXISTS). */
export function uidsUniques(uids: string[]): string[] {
  const vus = new Set<string>()
  return uids.map((u) => {
    let c = u
    for (let n = 2; vus.has(c); n++) {
      const suffixe = `-${n}`
      c = `${u.slice(0, 63 - suffixe.length).replace(/-+$/g, '')}${suffixe}`
    }
    vus.add(c)
    return c
  })
}

/** Ce que l'écran propose pour une ligne du lot de validation. */
export interface LigneValidation {
  id: string
  geste: 'accept' | 'attach'
  nom: string
  uid: string
  salle: string | null
  type: string | null
  /** Famille : obligatoire seulement pour le type « Autre ». */
  famille: string | null
  /** Rattachement : poste visé et « exemplaire remplacé ». */
  cible: string | null
  remplacer: boolean
  /** Niveau d'urgence (criticité de Sonde). */
  urgence: string
}

export function ligneInitiale(d: Decouverte): LigneValidation {
  const nom = d.suggested.name ?? d.hostname ?? d.value
  if (rattachementPossible(d) && d.attach_hint) {
    return {
      id: d.id, geste: 'attach', nom, uid: d.attach_hint.uid, salle: d.suggested.zone_code, type: d.suggested.kind, famille: d.suggested.family,
      cible: d.attach_hint.uid, remplacer: d.attach_hint.replacement, urgence: urgenceParDefaut(d.suggested.kind),
    }
  }
  return {
    id: d.id, geste: 'accept', nom,
    uid: d.suggested.uid ?? uidDepuisNom(nom, d.suggested.kind ?? 'poste'),
    salle: d.suggested.zone_code && CODE_SALLE.test(d.suggested.zone_code) ? d.suggested.zone_code : null,
    type: d.suggested.kind && TYPES_POSTE.includes(d.suggested.kind) ? d.suggested.kind : null,
    famille: d.suggested.family && d.suggested.family in LIBELLE_FAMILLE ? d.suggested.family : null,
    cible: null, remplacer: false, urgence: urgenceParDefaut(d.suggested.kind),
  }
}

/** Pourquoi une ligne ne peut pas partir (null = elle peut). */
export function problemeLigne(l: LigneValidation): string | null {
  if (l.geste === 'attach') return l.cible ? null : 'Poste à rattacher manquant.'
  if (!l.nom.trim()) return 'Nom manquant.'
  if (l.nom.trim().length > 200) return 'Nom trop long (200 caractères au plus).'
  if (!UID_POSTE.test(l.uid)) return 'Identifiant du poste : minuscules, chiffres et tirets, 2 à 63 caractères.'
  if (!l.type) return 'Choisissez le type du poste.'
  if (l.type === 'other' && !l.famille) return 'Type « Autre » : choisissez sa famille.'
  if (l.salle !== null && !CODE_SALLE.test(l.salle)) return 'Code de salle invalide.'
  return null
}

/** Le corps `POST discovery` d'un lot (200 au plus, ids uniques). */
export function decisionsValidation(lignes: LigneValidation[]): { id: string; action: 'accept' | 'attach'; [k: string]: unknown }[] {
  return lignes.map((l) => l.geste === 'attach' && l.cible
    ? { id: l.id, action: 'attach' as const, uid: l.cible, ...(l.remplacer ? { replace: true } : {}) }
    : {
        id: l.id, action: 'accept' as const,
        device: {
          uid: l.uid, display_name: l.nom.trim(), zone_code: l.salle, ...(l.type ? { kind: l.type } : {}),
          ...(l.type === 'other' && l.famille ? { family: l.famille } : {}),
          criticality: l.urgence, affects_global_health: pesePourUrgence(l.urgence),
        },
      })
}

export interface GroupeDecouvertes { cle: string; titre: string; items: Decouverte[] }

/** Groupe par type suggéré ou par source ; les plus nombreux d'abord, « type à choisir » en dernier. */
export function grouper(items: Decouverte[], par: 'type' | 'source'): GroupeDecouvertes[] {
  const m = new Map<string, Decouverte[]>()
  for (const d of items) {
    const cle = (par === 'type' ? d.suggested.kind : d.source) ?? ''
    const l = m.get(cle)
    if (l) l.push(d)
    else m.set(cle, [d])
  }
  const groupes = [...m.entries()].map(([cle, l]) => ({
    cle,
    titre: par === 'type' ? libelleGroupeType(cle || null) : libelleSource(cle || null),
    items: [...l].sort((a, b) => (a.suggested.zone_code ?? '~').localeCompare(b.suggested.zone_code ?? '~')
      || (a.suggested.name ?? a.value).localeCompare(b.suggested.name ?? b.value, 'fr')),
  }))
  return groupes.sort((a, b) => (a.cle === '' ? 1 : 0) - (b.cle === '' ? 1 : 0) || b.items.length - a.items.length || a.titre.localeCompare(b.titre, 'fr'))
}

/** Recherche locale (nom suggéré, valeur, fabricant, salle, type). */
export function correspond(d: Decouverte, q: string): boolean {
  const t = q.trim().toLowerCase()
  if (!t) return true
  return [d.suggested.name, d.value, d.vendor, d.hostname, d.suggested.zone_code, d.suggested.uid, libelleType(d.suggested.kind), libelleSource(d.source)]
    .some((x) => typeof x === 'string' && x.toLowerCase().includes(t))
}

/* ═══════════════ Import TECH_LC ═══════════════ */

/**
 * Limites : 512 Kio de texte (Sonde), et la requête entière 590 Kio (octets
 * du JSON, accents compris) — le relais de Gestion garde cette marge sous les
 * 600 Kio de Sonde.
 */
export const CSV_MAX_CARACTERES = 512 * 1024
export const ENVOI_MAX_OCTETS = 590 * 1024

/** Pourquoi ce CSV ne peut pas partir (null = il peut). */
export function problemeCsv(texte: string): string | null {
  if (!texte.trim()) return 'Le fichier est vide.'
  const octets = new TextEncoder().encode(JSON.stringify({ csv: texte, mode: 'preview' })).length
  if (texte.length > CSV_MAX_CARACTERES || octets > ENVOI_MAX_OCTETS) {
    return `Fichier trop gros (${Math.round(octets / 1024)} Kio à envoyer) : 590 Kio au plus par envoi. Découpez le tableur en plusieurs fichiers.`
  }
  return null
}

export const LIBELLE_ACTION_IMPORT: Record<ActionImport, string> = {
  create: 'Créations', update: 'Mises à jour', attach: 'Rattachements', replace: 'Remplacements', conflict: 'Conflits',
}

export const ORDRE_ACTIONS_IMPORT: readonly ActionImport[] = ['conflict', 'replace', 'create', 'update', 'attach']

export function grouperImport(items: ElementImport[]): { action: ActionImport; items: ElementImport[] }[] {
  return ORDRE_ACTIONS_IMPORT
    .map((action) => ({ action, items: items.filter((i) => i.action === action) }))
    .filter((g) => g.items.length > 0)
}

/** Exclusions qui survivent à un nouvel aperçu (ids stables d'un aperçu à l'autre, seulement les éléments applicables). */
export function exclusionsValides(exclus: ReadonlySet<string>, items: ElementImport[]): Set<string> {
  const applicables = new Set(items.filter((i) => i.applicable).map((i) => i.id))
  return new Set([...exclus].filter((id) => applicables.has(id)))
}

/** Combien d'éléments seront appliqués : applicables, non exclus ; un conflit, jamais. */
export function nombreAppliques(items: ElementImport[], exclus: ReadonlySet<string>): number {
  return items.filter((i) => i.applicable && i.action !== 'conflict' && !exclus.has(i.id)).length
}

/* ═══════════════ Propositions ═══════════════ */

/** Propositions du registre (le signalement Parc vit dans le Centre d'activité). */
export const SORTES_REGISTRE: readonly SorteProposition[] = ['power_domain', 'retire_ghost', 'replaced_unit']

/* ═══════════════ Fiche d'édition ═══════════════ */

export interface BrouillonPoste {
  display_name: string
  zone_code: string | null
  zone_detail: string
  criticality: string
  affects_global_health: boolean
  tracking: string
  visibility: string
  lifecycle: string
  notes: string
  /** Alimentation A puis B : '' = non renseignée, SECTEUR_SEUL, ou l'uid d'un onduleur. */
  alim_a: string
  alim_b: string
}

/** Valeur de saisie d'une alimentation branchée directement au secteur (non ondulée). */
export const SECTEUR_SEUL = '~secteur'

const saisieAlim = (feeds: readonly AlimentationRegistre[] | undefined, f: 'A' | 'B'): string => {
  const x = feeds?.find((a) => a.feed === f)
  return !x ? '' : x.ups_uid ?? SECTEUR_SEUL
}

/** Les alimentations saisies, dans la forme de Sonde (`power_feeds` remplace tout, 2 au plus). */
export function alimentationsDe(b: Pick<BrouillonPoste, 'alim_a' | 'alim_b'>): AlimentationRegistre[] {
  const out: AlimentationRegistre[] = []
  for (const [feed, v] of [['A', b.alim_a], ['B', b.alim_b]] as const) {
    if (v) out.push({ feed, ups_uid: v === SECTEUR_SEUL ? null : v })
  }
  return out
}

export function brouillonDe(p: PosteRegistre): BrouillonPoste {
  return {
    display_name: p.display_name, zone_code: p.zone_code, zone_detail: p.zone_detail ?? '', criticality: p.criticality,
    affects_global_health: p.affects_global_health, tracking: p.tracking, visibility: p.visibility, lifecycle: p.lifecycle,
    notes: p.notes ?? '', alim_a: saisieAlim(p.power_feeds, 'A'), alim_b: saisieAlim(p.power_feeds, 'B'),
  }
}

/** Le corps `PUT devices/:uid` : seulement ce qui a changé (Sonde n'écrit que l'écart). */
export function ecartPoste(p: PosteRegistre, b: BrouillonPoste): Record<string, unknown> {
  const avant = brouillonDe(p)
  const corps: Record<string, unknown> = {}
  if (b.display_name.trim() !== avant.display_name) corps['display_name'] = b.display_name.trim()
  if (b.zone_code !== avant.zone_code) corps['zone_code'] = b.zone_code
  if (b.zone_detail.trim() !== avant.zone_detail) corps['zone_detail'] = b.zone_detail.trim() || null
  if (b.criticality !== avant.criticality) corps['criticality'] = b.criticality
  if (b.affects_global_health !== avant.affects_global_health) corps['affects_global_health'] = b.affects_global_health
  if (b.tracking !== avant.tracking) corps['tracking'] = b.tracking
  if (b.visibility !== avant.visibility) corps['visibility'] = b.visibility
  if (b.lifecycle !== avant.lifecycle && b.lifecycle !== 'retired') corps['lifecycle'] = b.lifecycle
  if (b.notes.trim() !== avant.notes) corps['notes'] = b.notes.trim() || null
  // Une Sonde qui ne publie pas encore `power_feeds` ne reçoit jamais ce champ.
  if (p.power_feeds !== undefined && (b.alim_a !== avant.alim_a || b.alim_b !== avant.alim_b)) corps['power_feeds'] = alimentationsDe(b)
  return corps
}

export function problemePoste(b: BrouillonPoste): string | null {
  if (!b.display_name.trim()) return 'Le nom du poste est requis.'
  if (b.display_name.trim().length > 200) return 'Nom trop long (200 caractères au plus).'
  if (b.zone_code !== null && !CODE_SALLE.test(b.zone_code)) return 'Code de salle invalide.'
  if (b.zone_detail.length > 200) return 'Détail d’emplacement trop long (200 caractères au plus).'
  if (b.notes.length > 2000) return 'Notes trop longues (2 000 caractères au plus).'
  if (b.alim_b && !b.alim_a) return 'Renseignez d’abord l’alimentation A : la B ne vaut que pour un appareil à deux alimentations.'
  for (const v of [b.alim_a, b.alim_b]) if (v && v !== SECTEUR_SEUL && !UID_POSTE.test(v)) return 'Onduleur inconnu.'
  return null
}

/** Message d'un 409 DEVICE_IS_POWER_ROOT, conséquence d'abord. */
export function expliquerTemoin(message: string | null): string {
  return `${message ? `${message} ` : ''}Changez d’abord le témoin d’alimentation de la salle (Supervision › Salles et alimentation), puis recommencez.`
}
